import { Avatar, Button, cn, Icons } from "@win-reality/react-ui";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { useIsMobile } from "@/hooks/use-mobile";

import { Sheet, SheetContent } from "../sheet";

/**
 * NavMenu - A collapsible navigation menu with smooth animations
 *
 * Features:
 * - Collapsible menu groups with smooth animations
 * - Active state indicator with blue left border
 * - User profile section support
 * - Mobile-responsive with Sheet drawer
 * - Desktop collapse/expand with animated width transition
 */

const SIDEBAR_EXPANDED_PX = 256;
const SIDEBAR_COLLAPSED_PX = 72;
const SIDEBAR_CLIP_PX = SIDEBAR_EXPANDED_PX - SIDEBAR_COLLAPSED_PX;

export const SIDEBAR_WIDTH_EXPANDED = `${SIDEBAR_EXPANDED_PX}px`;
export const SIDEBAR_WIDTH_COLLAPSED = `${SIDEBAR_COLLAPSED_PX}px`;

// Context for NavMenu state
type NavMenuContextValue = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isMobile: boolean;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
};

const NavMenuContext = React.createContext<NavMenuContextValue | null>(null);

function useNavMenu() {
  const context = React.useContext(NavMenuContext);
  if (!context) {
    throw new Error("useNavMenu must be used within a NavMenuProvider");
  }
  return context;
}

// Types
export type NavMenuItemData = {
  id: string;
  label: string;
  url?: string;
  icon?: React.ComponentType<{ className?: string }>;
  children?: NavMenuItemData[];
  onClick?: () => void;
};

export type NavMenuUserData = {
  name: string;
  initials: string;
  avatarUrl?: string;
  onLogout?: () => void;
};

// Provider Props
type NavMenuProviderProps = {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
};

function NavMenuProvider({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  collapsed: controlledCollapsed,
  onCollapsedChange,
}: NavMenuProviderProps) {
  const isMobile = useIsMobile();
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [internalCollapsed, setInternalCollapsed] = React.useState(false);

  const isOpen = controlledOpen ?? internalOpen;
  const setIsOpen = React.useCallback(
    (open: boolean) => {
      if (onOpenChange) {
        onOpenChange(open);
      } else {
        setInternalOpen(open);
      }
    },
    [onOpenChange],
  );

  const isCollapsed = controlledCollapsed ?? internalCollapsed;
  const toggleCollapsed = React.useCallback(() => {
    const next = !isCollapsed;
    if (onCollapsedChange) {
      onCollapsedChange(next);
    } else {
      setInternalCollapsed(next);
    }
  }, [isCollapsed, onCollapsedChange]);

  const contextValue = React.useMemo(
    () => ({ isOpen, setIsOpen, isMobile, isCollapsed, toggleCollapsed }),
    [isOpen, setIsOpen, isMobile, isCollapsed, toggleCollapsed],
  );

  return (
    <NavMenuContext.Provider value={contextValue}>
      {children}
    </NavMenuContext.Provider>
  );
}

// Collapse/Expand button (desktop only, expanded panel only)
function NavMenuCollapseButton() {
  const { toggleCollapsed, isMobile } = useNavMenu();
  if (isMobile) return null;

  return (
    <div className="px-4 pb-2">
      <button
        onClick={toggleCollapsed}
        className={cn(
          // Layout
          "ml-auto flex items-center justify-center",
          // Spacing
          "p-2",
          // Border
          "rounded-md",
          // Text
          "text-gray-400",
          // Effects
          "transition-colors",
          // States
          "hover:bg-white/10 hover:text-white",
        )}
        aria-label="Collapse sidebar"
      >
        <ChevronLeft className="h-6 w-6" strokeWidth={2.5} />
      </button>
    </div>
  );
}

// Main NavMenu component
type NavMenuProps = {
  children: React.ReactNode;
  className?: string;
  logo?: React.ReactNode;
  collapsedLogo?: React.ReactNode;
  user?: NavMenuUserData;
  onLogoClick?: () => void;
};

function NavMenu({
  children,
  className,
  logo,
  collapsedLogo,
  user,
  onLogoClick,
}: NavMenuProps) {
  const { isOpen, setIsOpen, isMobile, isCollapsed, toggleCollapsed } =
    useNavMenu();

  const desktopCollapsed = !isMobile && isCollapsed;

  // Mobile: Use Sheet drawer
  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="left"
          className={cn(
            // Size
            "w-64",
            // Spacing
            "p-0",
            // Border
            "border-0",
            // Background
            "bg-[#24252C]",
            // Others
            "[&>button]:hidden",
          )}
        >
          <div className="flex h-full w-64 flex-col bg-[#24252C]">
            {logo && (
              <div
                className={cn("px-6 py-8", onLogoClick && "cursor-pointer")}
                onClick={onLogoClick}
                onKeyDown={
                  onLogoClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onLogoClick();
                        }
                      }
                    : undefined
                }
                role={onLogoClick ? "button" : undefined}
                tabIndex={onLogoClick ? 0 : undefined}
              >
                {logo}
              </div>
            )}
            <nav className="flex-1 overflow-y-auto px-4">{children}</nav>
            {user && <NavMenuUser {...user} />}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Fixed sidebar with animated clip wrapper.
  // Uses clip-path instead of overflow-hidden + width so internal layout is
  // completely stable — nothing reflows, only the visible area changes.
  // The outer container is pointer-events-none so the clipped region doesn't
  // block clicks on the main content behind it.
  return (
    <div
      className={cn(
        // Size
        "h-full",
        // Layout
        "fixed left-0 top-0 z-40",
        // Others
        "pointer-events-none",
        className,
      )}
    >
      {/* Clip wrapper — always full width internally, clip-path hides the right side */}
      <div
        className={cn(
          // Size
          "h-full",
          // Background
          "bg-[#24252C]",
          // Effects
          "transition-[clip-path] duration-[250ms] ease-in-out",
        )}
        style={{
          width: SIDEBAR_WIDTH_EXPANDED,
          clipPath: desktopCollapsed
            ? `inset(0 ${SIDEBAR_CLIP_PX}px 0 0)`
            : "inset(0 0px 0 0)",
        }}
      >
        {/* Expanded content — fixed width, never reflows */}
        <motion.div
          className="flex h-full flex-col"
          style={{
            width: SIDEBAR_WIDTH_EXPANDED,
            pointerEvents: desktopCollapsed ? "none" : "auto",
          }}
          animate={{ opacity: desktopCollapsed ? 0 : 1 }}
          transition={{
            duration: 0.1,
            delay: desktopCollapsed ? 0 : 0.2,
            ease: "easeOut",
          }}
          aria-hidden={desktopCollapsed}
        >
          {logo && (
            <div
              className={cn("px-6 py-8", onLogoClick && "cursor-pointer")}
              onClick={onLogoClick}
              onKeyDown={
                onLogoClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onLogoClick();
                      }
                    }
                  : undefined
              }
              role={onLogoClick ? "button" : undefined}
              tabIndex={onLogoClick ? 0 : undefined}
            >
              {logo}
            </div>
          )}
          <nav className="flex-1 overflow-y-auto px-4">{children}</nav>

          <div>
            {user && <NavMenuUser {...user} />}

            <NavMenuCollapseButton />
          </div>
        </motion.div>
      </div>

      {/* Collapsed overlay — sits on top; entire area is clickable to expand */}
      <motion.div
        className={cn(
          // Layout
          "group absolute inset-y-0 left-0 flex flex-col",
          // Effects
          "cursor-pointer transition-colors",
          // States
          "hover:bg-white/5",
        )}
        style={{
          width: SIDEBAR_WIDTH_COLLAPSED,
          pointerEvents: desktopCollapsed ? "auto" : "none",
        }}
        animate={{ opacity: desktopCollapsed ? 1 : 0 }}
        transition={{
          duration: 0.08,
          delay: desktopCollapsed ? 0.22 : 0,
        }}
        onClick={toggleCollapsed}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleCollapsed();
          }
        }}
        role="button"
        tabIndex={desktopCollapsed ? 0 : -1}
        aria-label="Expand sidebar"
      >
        {collapsedLogo && <div className="px-4 py-8">{collapsedLogo}</div>}
        <div className="flex-1" />
        <div className="px-4 py-2">
          <div
            className={cn(
              // Layout
              "flex items-center justify-center",
              // Spacing
              "p-2",
              // Border
              "rounded-md",
              // Text
              "text-gray-400",
              // Effects
              "transition-colors",
              // States
              "group-hover:bg-white/10 group-hover:text-white",
            )}
          >
            <ChevronRight className="h-6 w-6" strokeWidth={2.5} />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Sub-item component (for nested menu items)
type NavMenuSubItemProps = {
  item: NavMenuItemData;
  isActive: boolean;
  onNavigate?: (url: string) => void;
};

function NavMenuSubItem({ item, isActive, onNavigate }: NavMenuSubItemProps) {
  const { setIsOpen, isMobile } = useNavMenu();

  const handleClick = () => {
    if (item.url && onNavigate) {
      onNavigate(item.url);
      if (isMobile) {
        setIsOpen(false);
      }
    } else if (item.onClick) {
      item.onClick();
      if (isMobile) {
        setIsOpen(false);
      }
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        // Size
        "w-full",
        // Layout
        "group relative flex items-center overflow-hidden",
        // Spacing
        "px-3 py-2.5",
        // Border
        "rounded-sm",
        // Text
        "text-left text-sm text-white",
        // States
        "hover:bg-white/5",
        isActive && "bg-white/5",
      )}
    >
      {isActive && (
        <span className="absolute left-0 top-1/2 h-full w-1 -translate-y-1/2 rounded-full bg-blue-500" />
      )}
      {item.label}
    </button>
  );
}

// Top-level menu item component
type NavMenuItemProps = {
  item: NavMenuItemData;
  activeId?: string;
  onNavigate?: (url: string) => void;
};

function NavMenuItem({ item, activeId, onNavigate }: NavMenuItemProps) {
  const { setIsOpen, isMobile } = useNavMenu();
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.id === activeId;
  const hasActiveChild =
    hasChildren && item.children!.some((child) => child.id === activeId);
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Auto-expand if a child is active
  React.useEffect(() => {
    if (hasActiveChild) {
      setIsExpanded(true);
    }
  }, [hasActiveChild]);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    } else if (item.url && onNavigate) {
      onNavigate(item.url);
      if (isMobile) {
        setIsOpen(false);
      }
    } else if (item.onClick) {
      item.onClick();
      if (isMobile) {
        setIsOpen(false);
      }
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={cn(
          // Size
          "w-full",
          // Layout
          "group relative flex items-center justify-between overflow-hidden",
          // Spacing
          "px-3 py-2.5",
          // Border
          "rounded-sm",
          // Text
          "text-left text-base text-white",
          // Effects
          "transition-colors",
          // States
          "hover:bg-white/5",
          isActive && "bg-white/5",
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1/2 h-full w-1 -translate-y-1/2 rounded-full bg-blue-500" />
        )}
        <span className="flex items-center gap-3">
          {item.icon && <item.icon className="h-5 w-5 text-gray-400" />}
          {item.label}
        </span>

        {hasChildren && (
          <Icons.ChevronDown
            className={cn(
              // Size
              "h-4 w-4",
              // Text
              "text-gray-500",
              // Effects
              "transition-transform duration-200",
              // States
              isExpanded && "rotate-180",
            )}
          />
        )}
      </button>

      {/* Sub-items */}
      <AnimatePresence>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-1">
              {item.children!.map((child) => (
                <NavMenuSubItem
                  key={child.id}
                  item={child}
                  isActive={child.id === activeId}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Menu Group component
type NavMenuGroupProps = {
  items: NavMenuItemData[];
  activeId?: string;
  onNavigate?: (url: string) => void;
  className?: string;
};

function NavMenuGroup({
  items,
  activeId,
  onNavigate,
  className,
}: NavMenuGroupProps) {
  return (
    <div className={cn("space-y-1", className)}>
      {items.map((item) => (
        <NavMenuItem
          key={item.id}
          item={item}
          activeId={activeId}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

// User Profile Section
function NavMenuUser({ name, initials, avatarUrl, onLogout }: NavMenuUserData) {
  return (
    <div className="border-t border-white/5 px-4 pt-4 dark">
      <div className="flex flex-col items-center gap-3">
        <Avatar
          variant="default"
          initials={initials}
          imageSrc={avatarUrl}
          className="h-10 w-10 bg-gray-600 text-white"
        />
        <div className="flex flex-col justify-center items-center min-w-0">
          <p className="truncate text-base font-bold text-white">{name}</p>
          {onLogout && (
            <Button variant="ghost" size="xs" onClick={onLogout}>
              Log Out
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Spacer component
function NavMenuSpacer() {
  return <div className="my-4 border-t border-white/10" />;
}

export {
  NavMenu,
  NavMenuGroup,
  NavMenuItem,
  NavMenuProvider,
  NavMenuSpacer,
  NavMenuUser,
  useNavMenu,
};
